import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowLeft, FaUser, FaStethoscope, FaBed, FaSearch, FaRedo, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

import './NewAdmission.css';
import ENDPOINTS, { API_BASE_URL } from '../../Constants/ApiEndpoints';
import CameraCapture from '../../CommonComponents/CameraCapture';
import { doctorsNameToIdMap } from '../../Constants/DoctorsNameToIdMap';
import { toast } from 'react-toastify';

const days = Array.from({ length: 31 }, (_, i) => i + 1);
const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

const NewAdmission = ({ onBack }) => {
    const wrapperRef = useRef(null);
    const [billItems, setBillItems] = useState([]);
    const [image, setImage] = useState(null);

    const [showAdditional, setShowAdditional] = useState(true);
    const [lastChangedBy, setLastChangedBy] = useState(null); // 'dob' or 'age'

    const [searchResults, setSearchResults] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [patientIdentification, setPatientIdentification] = useState('MR No');

    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [billClearanceData, setBillClearanceData] = useState(null);

    // const currentDate = new Date().toISOString().split('T')[0]; // 'YYYY-MM-DD'
    const now = new Date();
    const currentDate = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0');
    const currentTime = new Date().toTimeString().split(' ')[0]; // 'HH:MM:SS'

    const [roomType, setRoomType] = useState('');
    const [roomOptions, setRoomOptions] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]);

    useEffect(() => {
        axios.get(ENDPOINTS.GET_ALL_ROOMS)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setAvailableRooms(res.data);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch rooms for admission", err);
            });
    }, []);

    const [doctors, setDoctors] = useState([]);
    useEffect(() => {
        axios.get(ENDPOINTS.GET_ALL_DOCTORS)
            .then((res) => {
                if (Array.isArray(res.data)) setDoctors(res.data);
            })
            .catch((err) => console.error("Failed to fetch doctors", err));
    }, []);

    const [referralType, setReferralType] = useState('');
    const [referredBy, setReferredBy] = useState('');

    // Derive dynamic room types list — purely from backend data, no hardcoded defaults
    const displayRoomTypes = Array.from(
        new Set(availableRooms.map(r => (r.roomType || '').trim().toUpperCase()).filter(Boolean))
    );

    // Calculate dynamic occupancy stats per room type from backend rooms
    const occupancyStats = displayRoomTypes.map(rt => {
        const roomsOfType = availableRooms.filter(r => (r.roomType || '').toUpperCase() === rt);
        const occupied = roomsOfType.reduce((acc, r) => acc + (Number(r.occupiedBeds) || (r.status === 'Occupied' ? Number(r.totalBeds || 1) : 0)), 0);
        const total = roomsOfType.reduce((acc, r) => acc + Number(r.totalBeds || 1), 0);
        const vacant = Math.max(0, total - occupied);
        return { roomType: rt, occupied, vacant, total };
    });

    const handleRoomTypeChange = (e) => {
        const selectedType = e.target.value;
        setRoomType(selectedType);
        handleChangeAdmission('roomType')({ target: { value: selectedType } });

        const matchingRooms = availableRooms.filter(
            (r) => (r.roomType || '').toUpperCase() === selectedType.toUpperCase() && r.status !== 'Under Maintenance'
        );

        let options = [];
        if (matchingRooms.length > 0) {
            matchingRooms.forEach((r) => {
                const total = Number(r.totalBeds || 1);
                const occupied = Number(r.occupiedBeds || 0);
                const vacantBeds = total - occupied;
                // r.floor is a HospitalFloor object — extract its floorName for display
                const floorLabel = r.floor?.floorName || r.floorName || 'N/A';
                const roomLabel = r.roomName || 'Room';
                if (vacantBeds > 0) {
                    if (total === 1) {
                        options.push(`${roomLabel} (${floorLabel})`);
                    } else {
                        for (let b = 1; b <= vacantBeds; b++) {
                            options.push(`${roomLabel} - Bed ${b} (${floorLabel})`);
                        }
                    }
                }
            });
        }

        // No hardcoded fallback — if no vacant rooms exist from backend, options stays empty

        setRoomOptions(options);
        handleChangeAdmission('bedNumber')({ target: { value: '' } });
    };

    const [patientPayload, setPatientPayload] = useState({
        title: '',
        photo: '',
        patientName: '',
        gender: '',
        phoneNumber: '',
        aadharNumber: '',
        clinicId: '',
        age: '',
        address: '',
        mailId: '',
        type: 'IN_PATIENT',
        dateOfBirth: '',
        patientIdentification: 'MR No', // Set default identification type as 'MR No'
        mrNumber: '', // make sure this exists
    });

    // State for admission Payload
    const [admissionPayload, setadmissionPayload] = useState({
        mrNumber: '',
        doctorId: '',
        // admissionDate: currentDate,
        patientType: 'IN_PATIENT',
        emergency: '',
        roomType: '', // ICU, GENERAL WARD, EMERGENCY WARD
        bedNumber: '', // Bed 1, Bed 2, etc.
        floor: '',
        // admissionTime: currentTime,
        admissionStatus: 'ADMITTED',
    });

    // State for Payment Payload
    const [paymentPayload, setPaymentPayload] = useState({
        id: '',
        admissionId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID'
    });

    // State for Payment Payload
    const [invoicePayload, setInvoicePayload] = useState({
        totalAmount: '',
        id: '',
        admissionId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID'
    });


    const validate = () => {
        const newErrors = {};

        if (!patientPayload.title) newErrors.title = 'Please enter the "Title"';
        if (!patientPayload.patientName) newErrors.patientName = 'Please enter the "Patient Name"';
        if (!patientPayload.gender) newErrors.gender = 'Please select the "Gender"';
        if (!patientPayload.ageYears) newErrors.ageYears = 'Please enter the "Age"';
        if (!admissionPayload.doctorId) newErrors.doctor = 'Please select a Doctor';
        if (!admissionPayload.emergency) newErrors.emergency = 'Please select "Is emergency?"';
        if (!admissionPayload.roomType) newErrors.roomType = 'Plesae enter the "Room Type"';
        if (!admissionPayload.bedNumber) newErrors.bedNumber = 'Plesae select the "Bed Number"';

        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach((msg) => toast.error(msg));
            return false;
        }

        return true;
    };



    // Handle change for Patient Payload
    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setPatientPayload((prev) => ({
            ...prev,
            [field]: value
        }));

        // Sync mrNumber with admissionPayload if it is related
        if (field === 'mrNumber') {
            setadmissionPayload((prev) => ({
                ...prev,
                mrNumber: value
            }));
        }
    };

    // Handle change for admission Payload
    const handleChangeAdmission = (field) => (e) => {
        const value = e.target.value;
        setadmissionPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle change for Payment Payload
    const handleChangePayment = (field) => (e) => {
        const value = e.target.value;
        setPaymentPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle change for Payment Payload
    const handleChangeInvoice = (field) => (e) => {
        const value = e.target.value;
        setInvoicePayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const getPlaceholder = (identification) => {
        switch (identification) {
            case 'MR No': return 'Enter Patient MR Number';
            case 'Phone': return 'Enter Patient Phone Number';
            case 'Aadhar': return 'Enter Patient Aadhar Number';
            case 'Clinic Id': return 'Enter Clinic ID';
            case 'Name': return 'Enter Patient Name';
            case 'Patient Id': return 'Enter Patient ID';
            default: return 'Enter Patient Details';
        }
    };

    // Fetch data based on selected identification type (like MR No, Name, etc.)
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const query = patientPayload.mrNumber.trim();
            if (query.length > 1) {
                let searchUrl = '';
                switch (patientPayload.patientIdentification) {
                    case 'MR No':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByMRNumber/${query}`;
                        break;
                    case 'Phone':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByPhoneNumber/${query}`;
                        break;
                    case 'Aadhar':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByAadhar/${query}`;
                        break;
                    case 'Clinic Id':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByClinicId/${query}`;
                        break;
                    case 'Name':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsBypatientName/${query}`;
                        break;
                    case 'Patient Id':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByPatientId/${query}`;
                        break;
                    default:
                        searchUrl = '';
                }

                if (searchUrl) {
                    axios
                        .get(searchUrl)
                        .then((res) => {
                            const data = res.data;
                            setSearchResults(Array.isArray(data) ? data : []);
                            setShowSuggestions(true);
                        })
                        .catch((err) => {
                            console.error("Search failed", err);
                            setSearchResults([]);
                            setShowSuggestions(true);
                        });
                }
            } else {
                setShowSuggestions(false);
                setSearchResults([]);
            }
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [patientPayload.mrNumber, patientPayload.patientIdentification]);




    const handlePatientChange = (field) => (e) => {
        const value = e.target.value;
        setPatientPayload(prev => ({
            ...prev,
            [field]: value
        }));
    };


    const formatInvoiceType = (type) => {
        if (!type || typeof type !== 'string') return 'BUG!!'; // or return 'Unknown' or any fallback

        const firstPart = type.split('_')[0]; // e.g., 'OUTPATIENT'
        return firstPart.charAt(0).toUpperCase() + firstPart.slice(1).toLowerCase();
    };

    // Callback to receive photo from CameraCapture
    const handlePhotoCapture = (base64Image) => {
        setPatientPayload(prev => ({ ...prev, photo: base64Image }));
    };



    const calculateDOBAndAge = (day, monthName, year) => {
        if (!day || !monthName || !year) return {};

        let dayNum = parseInt(day, 10);
        const monthIndex = months.indexOf(monthName);
        const yearNum = parseInt(year, 10);

        // Get max valid day for this month/year
        const maxDayInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
        if (dayNum > maxDayInMonth) {
            console.warn(`Clamping day from ${dayNum} to ${maxDayInMonth} for ${monthName} ${year}`);
            dayNum = maxDayInMonth;  // Clamp to last valid day
        } else {
            dayNum = parseInt(day); // Ensure we preserve user selection if valid
        }


        const dobDate = new Date(Date.UTC(yearNum, monthIndex, dayNum));
        const today = new Date();

        const dobString = `${yearNum}-${String(monthIndex + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

        let ageYears = today.getFullYear() - yearNum;
        let ageMonths = today.getMonth() - monthIndex;
        let ageDays = today.getDate() - dayNum;

        if (ageDays < 0) {
            ageMonths--;
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            ageDays += prevMonth.getDate();
        }

        if (ageMonths < 0) {
            ageYears--;
            ageMonths += 12;
        }

        return {
            dateOfBirth: dobString,
            ageYears,
            ageMonths,
            ageDays,
            age: ageYears,
            dobDay: dayNum.toString(), // Also update the form value if clamped
        };
    };


    const calculateDOBFromAge = (years, monthsValue, daysValue) => {
        if (isNaN(years) || isNaN(monthsValue) || isNaN(daysValue)) return {};

        const today = new Date();
        const dob = new Date(
            today.getFullYear() - years,
            today.getMonth() - monthsValue,
            today.getDate() - daysValue
        );

        const dobDay = dob.getUTCDate();
        const dobMonth = months[dob.getUTCMonth()];
        const dobYear = dob.getUTCFullYear();

        return {
            dobDay: dobDay.toString(),
            dobMonth: dobMonth,
            dobYear: dobYear.toString(),
            dateOfBirth: `${dobYear}-${String(dob.getUTCMonth() + 1).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`
        };
    };



    const handleDOBChange = (field) => (e) => {
        const updated = { ...patientPayload, [field]: e.target.value };
        const calc = calculateDOBAndAge(updated.dobDay, updated.dobMonth, updated.dobYear);

        setLastChangedBy('dob');
        setPatientPayload(prev => ({ ...updated, ...calc }));
    };




    useEffect(() => {
        if (lastChangedBy === 'age') {
            const { ageYears, ageMonths, ageDays } = patientPayload;
            const allFilled = ageYears !== '' && ageMonths !== '' && ageDays !== '';
            if (allFilled) {
                const calc = calculateDOBFromAge(
                    parseInt(ageYears),
                    parseInt(ageMonths),
                    parseInt(ageDays)
                );
                setPatientPayload(prev => ({ ...prev, ...calc }));
            }
        }
    }, [patientPayload.ageYears, patientPayload.ageMonths, patientPayload.ageDays]);


    const handleScheduleadmission = async () => {

        if (!validate()) {
            // If validation fails, stop here
            return;
        }

        // Clean null/empty string values from both payloads
        const cleanObject = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                    const trimmedValue = typeof value === 'string' ? value.trim() : value;
                    return [key, trimmedValue === '' ? null : trimmedValue];
                })
            );

        const cleanedPatient = cleanObject(patientPayload);
        const cleanedadmission = cleanObject(admissionPayload);
        const cleanedPayment = cleanObject(paymentPayload);
        const cleanedInvoice = cleanObject(invoicePayload);

        // Format timestamp
        const now = new Date();
        const pad = (num) => String(num).padStart(2, '0');
        const formattedDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

        // Send referralType and referral (referred-by) as separate fields
        // matching the two backend columns: referral_type and referral
        const referralPayload = {
            referralType: referralType || 'Self',
            referral: referralType && referralType !== 'Self' ? (referredBy || null) : null,
        };

        // Construct final payload
        const body = {
            patientPayload: {
                ...cleanedPatient,
                age: patientPayload.ageYears || patientPayload.age || 0, // include ageYears if available
            },
            admissionPayload: {
                ...cleanedadmission,
                timestamp: formattedDateTime,
                referralType: referralPayload.referralType,
                referral: referralPayload.referral,
            },
            paymentPayload: {
                ...cleanedPayment,
                admissionId: cleanedadmission.admissionId || null, // Include admissionId if available
                amount: cleanedPayment.amount || 0, // Default to 0 if not provided
            },
            invoicePayload: {
                ...cleanedInvoice,
                admissionId: cleanedadmission.admissionId || null, // Include admissionId if available
                totalAmount: cleanedInvoice.totalAmount || 0, // Default to 0 if not provided
            },
        };

        try {
            const res = await axios.post(ENDPOINTS.REGISTER_AND_ADMIT_PATIENT, body);

            if (res.status === 200) {
                toast.success("Patient Admitted successfully!");
                console.log(res.data);
                setTimeout(() => {
                    window.location.reload(); // Refresh the page
                }, 2000);
            } else {
                toast.error("Failed to Admit Patient");
            }
        } catch (error) {
            console.error("Error while Admitting the Patient", error);
            toast.error("Error while Admitting the Patient");
        }
    };




    return (
        <div className="form-container new-admission-page">
            <div className="breadcrumb">
                <div className="breadcrumb-title">
                    Inpatient / <strong>New Admission</strong>
                </div>
                <button onClick={onBack} className="custom-back-button">
                    <FaArrowLeft /> Back to Admissions
                </button>
            </div>


            <div className="mr-search">
                <div className="fused-search-group">
                    <select value={patientPayload.patientIdentification} onChange={handleChange('patientIdentification')}>
                        <option value="MR No">MR No</option>
                        <option value="Phone">Phone</option>
                        <option value="Name">Name</option>
                    </select>
                    <input
                        type="text"
                        placeholder={getPlaceholder(patientPayload.patientIdentification)}
                        value={patientPayload.mrNumber}
                        onChange={handleChange('mrNumber')}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    />

                    {showSuggestions && (
                        <ul className="suggestions-dropdown">
                            {searchResults.length > 0 ? (
                                searchResults.map((patient, idx) => (
                                    <li
                                        key={idx}
                                        onClick={async () => {
                                            setPatientPayload((prev) => ({
                                                ...prev,
                                                mrNumber: patient.mrNumber || '',
                                                patientName: patient.patientName || '',
                                                photo: patient.photo || '',

                                                gender: patient.gender || '',
                                                phoneNumber: patient.phoneNumber || '',
                                                ageYears: patient.age || '',
                                                mailId: patient.mailId || '',
                                                address: patient.address || '',
                                                aadharNumber: patient.aadharNumber || '',
                                                clinicId: patient.clinicId || '',
                                                title: patient.title || '',
                                                doctor: patient.doctor || '',

                                                dobDay: patient.dobDay || '',
                                                dobMonth: patient.dobMonth || '',
                                                dobYear: patient.dobYear || '',
                                                ageMonths: patient.ageMonths || '',
                                                ageDays: patient.ageDays || '',
                                                dateOfBirth: patient.dateOfBirth || '',
                                                doctorName: patient.doctorName || '',
                                                // medicineIdentifier: patient.billRequest.medicineIdentifier || 'Brand',
                                                // medicineName: patient.billRequest.medicineName || '',
                                            }));
                                            if (patient.photo) {
                                                setImage(`data:image/jpeg;base64,${patient.photo}`);
                                            }
                                            setadmissionPayload((prev) => ({
                                                ...prev,
                                                mrNumber: patient.mrNumber || '',
                                            }));

                                            // ✅ Call API if search was by MR number
                                            if (patientPayload.patientIdentification === 'MR No') {

                                                try {
                                                    const billClearanceResponse = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${patient.mrNumber}`);
                                                    console.log("Extra patient data:", billClearanceResponse.data);
                                                    // You can update state with this extra data if needed
                                                    setBillClearanceData(billClearanceResponse.data); // Store in state
                                                } catch (error) {
                                                    console.error("Failed to fetch full patient details:", error);
                                                }
                                            }
                                            setShowSuggestions(false);
                                        }}
                                    >
                                        {patient.mrNumber} ({patient.patientName})  - {patient.phoneNumber}
                                    </li>
                                ))
                            ) : (
                                <li className="no-results">No matching patients found</li>
                            )}
                        </ul>
                    )}
                </div>

                <button className="alt-s hide-sm">ALT + S</button>
                <button className="reset">reset</button>
            </div>

            {billClearanceData && (
                <div className="bill-clearance-box">
                    <h4>Bill Clearance Info</h4>
                    <p>Total Due Amount: ₹{billClearanceData.totalDueAmount}</p>
                    {billClearanceData?.invoices?.length > 0 && (
                        <p>
                            Due Payments:{' '}
                            {[...new Set(
                                billClearanceData.invoices.map((invoice) =>
                                    formatInvoiceType(invoice.invoiceType)
                                )
                            )].join(', ')}
                        </p>
                    )}

                    {/* Add more fields as needed */}
                </div>
            )}

            {/* <div className="profile-photo">
                <div className="avatar">
                    <span className="camera-icon">📷</span>
                </div>
            </div> */}
            {/* <CameraCapture onCapture={handlePhotoCapture} /> */}
            <CameraCapture image={image} setImage={setImage} onCapture={handlePhotoCapture} />


            <div className="form-body">
                <div className="section-header-title">
                    <FaUser color="#2563eb" size={15} /> Patient Personal Details
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>* Name</label>
                        <div className="input-pair">
                            <select value={patientPayload.title} onChange={handleChange('title')}>
                                <option value="">Title</option>
                                <option>Mr</option>
                                <option>Mrs</option>
                                <option>Miss</option>
                                <option>Dr</option>
                            </select>
                            <input type="text" placeholder="Name" value={patientPayload.patientName} onChange={handleChange('patientName')} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>* Gender</label>
                        <select value={patientPayload.gender} onChange={handleChange('gender')}>
                            <option value="">Select Gender</option>
                            <option>Male</option>
                            <option>Female</option>
                            <option>Other</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>* Phone</label>
                        <input type="text" placeholder="Phone" value={patientPayload.phoneNumber} onChange={handleChange('phoneNumber')} />
                    </div>
                    <div className="form-group">
                        <label>Aadhar</label>
                        <input type="text" placeholder="Aadhar" value={patientPayload.aadharNumber} onChange={handleChange('aadharNumber')} />
                    </div>
                    <div className="form-group">
                        <label>Clinic ID</label>
                        <input type="text" placeholder="Clinic ID" value={patientPayload.clinicId} onChange={handleChange('clinicId')} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>* Date Of Birth</label>
                        <div className="dob-inputs" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <select value={patientPayload.dobDay} onChange={handleDOBChange('dobDay')}>
                                <option value="">Day</option>
                                {days.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <select value={patientPayload.dobMonth} onChange={handleDOBChange('dobMonth')}>
                                <option value="">Month</option>
                                {months.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <select value={patientPayload.dobYear} onChange={handleDOBChange('dobYear')}>
                                <option value="">Year</option>
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <span>(or)</span>
                            <input type="number" placeholder="Years" value={patientPayload.ageYears} onChange={handleChange('ageYears')} />
                            <input type="number" placeholder="Months" value={patientPayload.ageMonths} onChange={handleChange('ageMonths')} />
                            <input type="number" placeholder="Days" value={patientPayload.ageDays} onChange={handleChange('ageDays')} />
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group wide">
                        <label>Address</label>
                        <input type="text" placeholder="Address" value={patientPayload.address} onChange={handleChange('address')} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group wide">
                        <label>Email</label>
                        <input type="email" placeholder="Email" value={patientPayload.mailId} onChange={handleChange('mailId')} />
                    </div>
                </div>
            </div>



            {/* IP SECTION DETAILS START */}
            <div className="form-body">
                <div className="section-header-title">
                    <FaStethoscope color="#2563eb" size={15} /> Admission & Doctor Details
                </div>
                <div className="form-row">

                    <div className="form-group">
                        <label>IP No :</label>
                        <span className="ip-number">000078/2025</span>
                    </div>

                    <div className="form-group flex-grow">
                        <label>Doctor</label>
                        <select
                            className="select-doctor-dropdown"
                            value={
                                Object.keys(doctorsNameToIdMap).find(
                                    key => doctorsNameToIdMap[key] === admissionPayload.doctorId
                                ) || ''
                            }
                            onChange={(e) => {
                                const selectedName = e.target.value;
                                const doctorId = doctorsNameToIdMap[selectedName];
                                if (doctorId) {
                                    setadmissionPayload(prev => ({
                                        ...prev,
                                        doctorId: doctorId
                                    }));
                                }
                            }}
                        >
                            <option value="">Select doctor</option>
                            <option value="Dr. Vishwanath">Dr. Vishwanath</option>
                            <option value="Dr. Sushma">Dr. Sushma</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Emergency Admission?</label>
                        <div className="radio-group emergency-toggle-group">
                            <label className={`radio-pill-btn emergency-yes ${admissionPayload.emergency === 'TRUE' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="emergency"
                                    id="emergency-yes"
                                    value="TRUE"
                                    checked={admissionPayload.emergency === 'TRUE'}
                                    onChange={handleChangeAdmission('emergency')}
                                />
                                <FaExclamationTriangle size={12} /> Yes (Emergency)
                            </label>
                            <label className={`radio-pill-btn emergency-no ${admissionPayload.emergency === 'FALSE' ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name="emergency"
                                    id="emergency-no"
                                    value="FALSE"
                                    checked={admissionPayload.emergency === 'FALSE'}
                                    onChange={handleChangeAdmission('emergency')}
                                />
                                <FaCheckCircle size={12} /> No (General)
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Referral Type</label>
                        <select value={referralType} onChange={(e) => { setReferralType(e.target.value); setReferredBy(''); }}>
                            <option value="">-- Select Referral Type --</option>
                            <option value="Doctor Referral">Doctor Referral</option>
                            <option value="Advertisement">Advertisement</option>
                            <option value="Self">Self</option>
                            <option value="Marketing">Marketing</option>
                            <option value="Online">Online</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>


                    <div className="form-group">
                        <label>Referred By{referralType === 'Self' && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}> (not applicable)</span>}</label>

                        {/* Self → disabled greyed text box */}
                        {(!referralType || referralType === 'Self') && (
                            <input
                                type="text"
                                placeholder={referralType === 'Self' ? 'Not applicable for Self referral' : 'Select a referral type first'}
                                value={referredBy}
                                disabled
                                style={{ opacity: 0.45, cursor: 'not-allowed', background: '#f1f5f9' }}
                            />
                        )}

                        {/* Doctor Referral → free text */}
                        {referralType === 'Doctor Referral' && (
                            <input
                                type="text"
                                placeholder="Enter referring doctor name"
                                value={referredBy}
                                onChange={(e) => setReferredBy(e.target.value)}
                            />
                        )}

                        {/* Advertisement → dropdown */}
                        {referralType === 'Advertisement' && (
                            <select value={referredBy} onChange={(e) => setReferredBy(e.target.value)}>
                                <option value="">Select Channel</option>
                                <option value="TV">TV</option>
                                <option value="Radio">Radio</option>
                                <option value="Newspaper">Newspaper</option>
                                <option value="Hoarding">Hoarding / Billboard</option>
                                <option value="Pamphlet">Pamphlet / Flyer</option>
                            </select>
                        )}

                        {/* Marketing → dropdown */}
                        {referralType === 'Marketing' && (
                            <select value={referredBy} onChange={(e) => setReferredBy(e.target.value)}>
                                <option value="">Select Source</option>
                                <option value="Health Camp">Health Camp</option>
                                <option value="Corporate Tie-up">Corporate Tie-up</option>
                                <option value="Insurance Tie-up">Insurance Tie-up</option>
                                <option value="Community Outreach">Community Outreach</option>
                            </select>
                        )}

                        {/* Online → dropdown */}
                        {referralType === 'Online' && (
                            <select value={referredBy} onChange={(e) => setReferredBy(e.target.value)}>
                                <option value="">Select Platform</option>
                                <option value="Hospital Website">Hospital Website</option>
                                <option value="Google">Google Search</option>
                                <option value="Facebook">Facebook</option>
                                <option value="Instagram">Instagram</option>
                                <option value="Practo">Practo</option>
                                <option value="JustDial">JustDial</option>
                            </select>
                        )}

                        {/* Other → free text */}
                        {referralType === 'Other' && (
                            <input
                                type="text"
                                placeholder="Specify referral source"
                                value={referredBy}
                                onChange={(e) => setReferredBy(e.target.value)}
                            />
                        )}
                    </div>


                    <div className="form-group">
                        <label >Ip CaseType</label>
                        <select>
                            <option>Select CaseType</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Brought by</label>
                        <input type="text" placeholder="Brought by" />
                    </div>

                    <div className="form-group">
                        <label >Op casefile Reference</label>
                        <input type="text" />
                    </div>

                </div>
            </div>


            <div className="form-body">
                <div className="section-header-title">
                    <FaBed color="#2563eb" size={15} /> Room Occupancy & Bed Selection
                </div>
                <div className="form-row">

                    {/* SECTION 2: Room Table and Room Selection */}
                    <div className="room-info-section">
                        <div className="room-info-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Room type</th>
                                        <th>Occupied</th>
                                        <th>Vacant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {occupancyStats.map((stat, i) => (
                                        <tr key={i}>
                                            <td>{stat.roomType}</td>
                                            <td>{stat.occupied}</td>
                                            <td>{stat.vacant}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>



                <div className="form-row">

                    <div className="form-group">
                        <label>Room type</label>
                        <select value={roomType} onChange={handleRoomTypeChange}>
                            <option value="">Select Room type</option>
                            {displayRoomTypes.map((type, index) => (
                                <option key={index} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Bed</label>
                        <select value={admissionPayload.bedNumber} onChange={handleChangeAdmission('bedNumber')}>
                            <option value="">Select Bed</option>
                            {roomType && roomOptions.length === 0 && (
                                <option value="" disabled>No rooms available</option>
                            )}
                            {roomOptions.map((bed, index) => (
                                <option key={index} value={bed}>
                                    {bed}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

            </div>


            {/* IP SECTION DETAILS END */}

            <div className="create-request-wrapper">
                <button className="btn-secondary" onClick={onBack}>
                    Cancel
                </button>
                <button className="btn-primary" onClick={handleScheduleadmission}>
                    Admit Patient
                </button>
            </div>
        </div>
    );
};

export default NewAdmission;
